import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  useMediaQuery,
  Stack,
  CardActions,
} from '@mui/material';
import {
  CheckCircle,
  CreditCard,
  TrendingUp,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import httpClient from '../services/httpClient';

const Subscriptions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [expiredSubscriptions, setExpiredSubscriptions] = useState([]);
  const [statistics, setStatistics] = useState({
    activeCount: 0,
    expiredCount: 0,
    monthlyRevenue: 0,
    totalSubscriptions: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [active, expired, stats] = await Promise.all([
        httpClient.get('/v1/admin/subscriptions/active'),
        httpClient.get('/v1/admin/subscriptions/expired'),
        httpClient.get('/v1/admin/subscriptions/stats'),
      ]);

      setActiveSubscriptions(active.data);
      setExpiredSubscriptions(expired.data);
      setStatistics(stats.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
      setError('Failed to load subscription data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (subscription) => {
    setSelectedSub(subscription);
    setDetailsOpen(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'CANCELLED':
        return 'error';
      case 'TRIAL':
        return 'warning';
      case 'SUSPENDED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper function to render subscription cards (mobile)
  const renderSubscriptionCard = (sub) => (
    <Card key={sub.subscriptionId} sx={{ mb: 2 }}>
      <CardContent>
        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Box>
            {sub.users && sub.users.length > 0 ? (
              <>
                <Typography variant="body1" fontWeight="bold">
                  {sub.users[0].firstName && sub.users[0].lastName
                    ? `${sub.users[0].firstName} ${sub.users[0].lastName}`
                    : sub.users[0].username}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {sub.users[0].email}
                </Typography>
                {sub.users.length > 1 && (
                  <Typography variant="caption" color="primary">
                    {` +${sub.users.length - 1} more`}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No user
              </Typography>
            )}
          </Box>
        </Box>

        {/* Status & Plan */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label={sub.status} color={getStatusColor(sub.status)} size="small" />
          <Chip label={sub.planType} color="primary" size="small" variant="outlined" />
        </Stack>

        {/* Details */}
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="textSecondary">Start Date</Typography>
            <Typography variant="body2">{formatDate(sub.startDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">End Date</Typography>
            <Typography variant="body2">{formatDate(sub.endDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Price</Typography>
            <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
              ${sub.price?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
      <CardActions>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleViewDetails(sub)}
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <CreditCard sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Subscription Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage and monitor all active and expired user subscriptions
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.success.light}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ color: theme.palette.success.main, mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Active Subscriptions
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                  {statistics.activeCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.error.light}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography color="textSecondary" variant="body2">
                    Expired Subscriptions
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                  {statistics.expiredCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.info.light}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUp sx={{ color: theme.palette.info.main, mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Monthly Revenue
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                  ${statistics.monthlyRevenue?.toFixed(2) || '0.00'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.primary.light}`,
            }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" sx={{ mb: 1 }}>
                  Total Subscriptions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  {statistics.totalSubscriptions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, boxShadow: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label={`Active (${activeSubscriptions.length})`} />
          <Tab label={`Expired (${expiredSubscriptions.length})`} />
        </Tabs>
      </Paper>

      {/* Tab Content - Active Subscriptions */}
      {activeTab === 0 && (
        isMobile ? (
          // Mobile Card View
          <Box>
            {activeSubscriptions.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">No active subscriptions</Typography>
              </Paper>
            ) : (
              activeSubscriptions.map((sub) => renderSubscriptionCard(sub))
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No active subscriptions</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activeSubscriptions.map((sub) => (
                    <TableRow key={sub.subscriptionId} hover>
                      <TableCell>
                        {sub.users && sub.users.length > 0 ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {sub.users[0].firstName && sub.users[0].lastName
                                ? `${sub.users[0].firstName} ${sub.users[0].lastName}`
                                : sub.users[0].username}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {sub.users[0].email}
                            </Typography>
                            {sub.users.length > 1 && (
                              <Typography variant="caption" color="primary">
                                {` +${sub.users.length - 1} more`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No user
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.planType} color="primary" size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.status} color="success" size="small" />
                      </TableCell>
                      <TableCell>{formatDate(sub.startDate)}</TableCell>
                      <TableCell>{formatDate(sub.endDate)}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          ${sub.price?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(sub)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Tab Content - Expired Subscriptions */}
      {activeTab === 1 && (
        isMobile ? (
          // Mobile Card View
          <Box>
            {expiredSubscriptions.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">No expired subscriptions</Typography>
              </Paper>
            ) : (
              expiredSubscriptions.map((sub) => renderSubscriptionCard(sub))
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No expired subscriptions</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  expiredSubscriptions.map((sub) => (
                    <TableRow key={sub.subscriptionId} hover>
                      <TableCell>
                        {sub.users && sub.users.length > 0 ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {sub.users[0].firstName && sub.users[0].lastName
                                ? `${sub.users[0].firstName} ${sub.users[0].lastName}`
                                : sub.users[0].username}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {sub.users[0].email}
                            </Typography>
                            {sub.users.length > 1 && (
                              <Typography variant="caption" color="primary">
                                {` +${sub.users.length - 1} more`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No user
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.planType} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.status} color="error" size="small" />
                      </TableCell>
                      <TableCell>{formatDate(sub.startDate)}</TableCell>
                      <TableCell>{formatDate(sub.endDate)}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 'bold' }}>
                          ${sub.price?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(sub)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Subscription Details
          </Typography>
          <Button
            icon="close"
            onClick={() => setDetailsOpen(false)}
            sx={{ minWidth: 'auto' }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSub && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* User Information Section */}
              {selectedSub.users && selectedSub.users.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05), 
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    USER{selectedSub.users.length > 1 ? 'S' : ''}
                  </Typography>
                  {selectedSub.users.map((user, index) => (
                    <Box key={user.userId} sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {user.email}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  SUBSCRIPTION ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                  {selectedSub.subscriptionId}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  PLAN TYPE
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedSub.planType} color="primary" size="small" />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  SUBSCRIPTION STATUS
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={selectedSub.isActive ? selectedSub.status : (selectedSub.status === 'ACTIVE' ? 'EXPIRED' : selectedSub.status)} 
                    color={selectedSub.isActive ? getStatusColor(selectedSub.status) : 'error'} 
                    size="small" 
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  PRICE
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold', fontSize: '1.1rem' }}>
                  ${selectedSub.price?.toFixed(2) || '0.00'}/month
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  MAX GATEWAYS
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedSub.maxGateways || 'Unlimited'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  MAX LOCATIONS PER GATEWAY
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedSub.maxLocationsPerGateway || 'Unlimited'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  START DATE
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatDate(selectedSub.startDate)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  END DATE
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatDate(selectedSub.endDate)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  ACCESS STATUS
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedSub.isActive ? 'Has Access' : (selectedSub.status === 'ACTIVE' ? 'Expired (Past End Date)' : 'No Access')}
                    color={selectedSub.isActive ? 'success' : 'error'}
                    size="small"
                  />
                  {!selectedSub.isActive && selectedSub.status === 'ACTIVE' && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      End date has passed - subscription needs renewal
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Subscriptions;
